import { Component, OnDestroy, OnInit } from '@angular/core'
import { CommonModule, DatePipe } from '@angular/common'
import { MarriottViewBillWidget, ProjectCMSWidgetType, SpecificActionSubType, LogService } from 'htv-lib'
import { BaseWidgetComponent } from '../base-widget/base-widget.component'
import { StateService } from '../../services/state.service'
import { LocationService } from '../../services/location.service'
import { TranslateModule } from '@ngx-translate/core'
import { Bill, BillOrderedItem } from '../../interfaces/location'
import { CentsToCurrencyPipe } from '../../pipes/cents-to-currency.pipe'
import { WidgetActionService } from '../../services/widget-action.service'

@Component({
  selector: 'app-marriott-view-bill',
  standalone: true,
  imports: [CommonModule, TranslateModule, DatePipe, CentsToCurrencyPipe],
  templateUrl: './marriott-view-bill.component.html',
  styleUrls: ['./marriott-view-bill.component.scss'],
})
export class MarriottViewBillComponent extends BaseWidgetComponent implements OnInit, OnDestroy {
  bill?: Bill
  billError = false

  itemsPerPage = 5
  currentPage = 0
  paginatedItems: BillOrderedItem[] = []

  totalPages = 0
  hasPreviousPage = false
  hasNextPage = false
  hasAnyDates = false
  hasAnyQuantities = false

  constructor(
    private stateService: StateService,
    private locationService: LocationService,
    private widgetActionService: WidgetActionService,
    private logService: LogService
  ) {
    super()
  }

  override ngOnInit(): void {
    super.ngOnInit()
    this.stateService.startShowingWidget(ProjectCMSWidgetType.MARRIOTT_VIEW_BILL)
    this.registerWidgetActionListener()

    if (this.locationService.location?.id) {
      this.subscriptions.add(
        this.locationService.getClientBill(this.locationService.location.id).subscribe({
          next: bill => {
            this.bill = bill
            this.updatePaginatedItems()
          },
          complete: () => {
            if (!this.bill) {
              this.billError = true
            }
          },
        })
      )
    }
  }

  override ngOnDestroy(): void {
    this.subscriptions.unsubscribe()
    this.stateService.stopShowingWidget(ProjectCMSWidgetType.MARRIOTT_VIEW_BILL)
    super.ngOnDestroy()
  }

  registerWidgetActionListener(): void {
    this.subscriptions.add(
      this.widgetActionService.billWidgetAction$.subscribe(action => {
        this.handleWidgetAction(action)
      })
    )
  }

  handleWidgetAction(action: SpecificActionSubType): void {
    switch (action) {
      case SpecificActionSubType.BILL_NEXT_PAGE:
        this.nextPage()
        break
      case SpecificActionSubType.BILL_PREVIOUS_PAGE:
        this.previousPage()
        break
      case SpecificActionSubType.BILL_EXPRESS_CHECKOUT:
        this.expressCheckout()
        break
    }
  }

  expressCheckout(): void {
    this.logService.debug('[MarriottViewBillComponent] Express Checkout triggered')
    // TODO: Implement express checkout logic
  }

  updatePaginatedItems(): void {
    if (!this.bill) {
      this.paginatedItems = []
      this.updatePaginationState()
      return
    }
    const startIndex = this.currentPage * this.itemsPerPage
    const endIndex = startIndex + this.itemsPerPage
    this.paginatedItems = this.bill.billOrderedItems.slice(startIndex, endIndex)
    this.updatePaginationState()
  }

  updatePaginationState(): void {
    if (!this.bill) {
      this.totalPages = 0
      this.hasPreviousPage = false
      this.hasNextPage = false
      this.hasAnyDates = false
      this.hasAnyQuantities = false
      return
    }

    this.totalPages = Math.ceil(this.bill.billOrderedItems.length / this.itemsPerPage)
    this.hasPreviousPage = this.currentPage > 0
    this.hasNextPage = this.currentPage < this.totalPages - 1
    this.hasAnyDates = this.bill.billOrderedItems.some(item => item.date != null)
    this.hasAnyQuantities = this.bill.billOrderedItems.some(item => item.quantity != null)
  }

  previousPage(): void {
    if (this.hasPreviousPage) {
      this.currentPage--
      this.updatePaginatedItems()
    }
  }

  nextPage(): void {
    if (this.hasNextPage) {
      this.currentPage++
      this.updatePaginatedItems()
    }
  }

  override applyStyles(): void {
    super.applyStyles()

    const config = this.config as MarriottViewBillWidget
    const widgetStyle: Record<string, string | number> = {}
    const headerStyle: Record<string, string | number> = {}
    const contentStyle: Record<string, string | number> = {}
    const billTableStyle: Record<string, string | number> = {}
    const actionsStyle: Record<string, string | number> = {}
    const navButtonStyle: Record<string, string | number> = {}
    const navButtonSelectedStyle: Record<string, string | number> = {}

    if (config.backgroundColor) {
      headerStyle['background-color'] = config.backgroundColor
      contentStyle['background-color'] = config.backgroundColor
      actionsStyle['background-color'] = config.backgroundColor
    }

    if (config.textColor) {
      widgetStyle['color'] = config.textColor
      billTableStyle['color'] = config.textColor
      navButtonStyle['color'] = config.textColor
      navButtonStyle['border-color'] = config.textColor
    }

    if (config.buttonColor) {
      navButtonSelectedStyle['border-color'] = config.buttonColor
    }

    this.setRule('.view-bill-widget', widgetStyle)
    this.setRule('.header', headerStyle)
    this.setRule('.content', contentStyle)
    this.setRule('.bill-table', billTableStyle)
    this.setRule('.actions', actionsStyle)
    this.setRule('.nav-button', navButtonStyle)
    this.setRule('.nav-button.htv-selected:not(.disabled)', navButtonSelectedStyle)
  }
}
